// Line Entry — wire contract with the engine (`fetch_lines`, `recalc_lines`,
// `list_documents`, `apply_lines` on the execute funnel). Both engines (Python
// `line_entry.py`, Java `core/LineEntry.java`) answer this shape.

import type { ResolvedLineLimit } from './model'

export interface LineWarning { col?: string | null; message: string; code?: string }

/** One candidate / produced line, as the engine returns it. */
export interface LineRowResult {
  _src_key: string
  values: Record<string, unknown>
  _display?: Record<string, unknown>
  _limits?: Record<string, ResolvedLineLimit>
  _linked?: boolean
  _selected?: boolean
  _errors?: Record<string, string>
  _row_error?: string
  _warnings?: LineWarning[]
  _protect?: Record<string, 0 | 1>
  _status?: { column: string; index: number; label: string; delta?: number }
  _show?: Record<string, unknown>
  /** The document the row came from — the picker groups by it. */
  _document?: unknown
  /** apply_lines only: where this line came from (kept on the buffer row). */
  _link?: { source: string; key: string }
  seq?: number
}

export interface LinePickerColumn {
  key: string
  kind: 'line' | 'source' | 'extra'
  label: string
  col_type?: string
  editable: boolean
  status: boolean
}

export interface FetchLinesResponse {
  ok: boolean
  command: string
  source: string
  source_label?: string
  command_label?: string
  kind: string
  picker: 'none' | 'review' | 'multi'
  documents: unknown[]
  columns: LinePickerColumn[]
  rows: LineRowResult[]
  total: number
  offset: number
  message?: string
  options: {
    validate: 'limits' | 'object'
    on_invalid: 'block_add' | 'skip_invalid'
    item_change: boolean
    edit: string[]
    totals?: string[]
    on_document_change: 'ask' | 'replace' | 'keep'
  }
}

export interface ApplyLinesResponse {
  ok: boolean
  command: string
  source: string
  form_no: string
  rows: LineRowResult[]
  options: { on_invalid: 'block_add' | 'skip_invalid' }
}

export interface ListDocumentsResponse {
  ok: boolean
  rows: Array<{ id: unknown; display: string; _on_record?: boolean }>
  total: number
  multi: boolean
}

/** One criterion of a General Process source, as the criteria step shows it
 *  (`line_criteria`): pre-filled from the line source's mapping or the
 *  process's own default; `protected` ones are read-only and the engine never
 *  takes them from the browser. */
export interface LineCriterion {
  name: string
  label?: string
  data_type: 'string' | 'date' | 'number'
  operator?: string
  multi: 'single' | 'list' | 'range'
  value?: unknown
  default_helper?: string
  protected: boolean
  mapped: boolean
  has_lookup?: boolean
}

export interface LineCriteriaResponse {
  ok: boolean
  command: string
  /** False → no criteria step: straight to the lines. */
  ask: boolean
  process?: string
  source_label?: string
  criteria: LineCriterion[]
}

export interface RecalcRequestRow {
  _src_key: string
  values: Record<string, unknown>
  changed: string[]
  seq: number
}

/** The execute funnel, bound to the transaction object by the host surface. */
export type LineExecute = (action: string, payload: Record<string, unknown>) => Promise<unknown>

export interface LineEntryScope {
  formNo: string
  headerValues: Record<string, unknown>
  /** The record being edited (absent for a new one). */
  txnId?: string
  /** The line form's parent key — lets the engine read saved lines it was
   *  not handed (an edited record whose lines are not all in the buffer). */
  parentPk?: Record<string, unknown>
  /** The lines already on the record (this form's buffer rows). */
  lines: Array<Record<string, unknown>>
}

function base(scope: LineEntryScope, command: string,
  criteria?: Record<string, unknown>): Record<string, unknown> {
  const lines = scope.lines
    .filter(r => !r._deleted)
    .map((r) => {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(r)) {
        if (k === '__children' || k.startsWith('_') && k !== '_txn_id') continue
        out[k] = v
      }
      return out
    })
  return {
    form_no: scope.formNo,
    command,
    header_values: scope.headerValues || {},
    details: { [scope.formNo]: lines },
    ...(scope.txnId ? { txn_id: scope.txnId } : {}),
    ...(scope.parentPk && Object.keys(scope.parentPk).length ? { parent_pk: scope.parentPk } : {}),
    ...(criteria && Object.keys(criteria).length ? { criteria } : {}),
  }
}

/** The line-entry calls for one form. `criteria` — the user's choices on a
 *  General Process source's criteria step — rides every read of that run, so
 *  the picker, its recalcs and the final apply all see the same rows. */
export function lineEntryApi(execute: LineExecute, scope: LineEntryScope,
  criteria?: Record<string, unknown>) {
  const b = (command: string) => base(scope, command, criteria)
  return {
    criteria(command: string, documents?: unknown[]) {
      return execute('line_criteria', {
        ...base(scope, command), ...(documents ? { documents } : {}),
      }) as Promise<LineCriteriaResponse>
    },
    /** The same calls bound to a criteria step's values. */
    withCriteria(values: Record<string, unknown>) {
      return lineEntryApi(execute, scope, values)
    },
    fetch(command: string, opts: { documents?: unknown[]; search?: string; offset?: number; limit?: number } = {}) {
      return execute('fetch_lines', {
        ...b(command),
        ...(opts.documents ? { documents: opts.documents } : {}),
        ...(opts.search ? { search: opts.search } : {}),
        ...(opts.offset ? { offset: opts.offset } : {}),
        ...(opts.limit ? { limit: opts.limit } : {}),
      }) as Promise<FetchLinesResponse>
    },
    recalc(command: string, rows: RecalcRequestRow[], documents?: unknown[]) {
      return execute('recalc_lines', {
        ...b(command), rows, ...(documents ? { documents } : {}),
      }) as Promise<{ ok: boolean; rows: LineRowResult[] }>
    },
    documents(command: string, search = '') {
      return execute('list_documents', {
        ...b(command), ...(search ? { search } : {}),
      }) as Promise<ListDocumentsResponse>
    },
    apply(
      command: string,
      picks: Array<{ _src_key: string; values?: Record<string, unknown> }> | 'all',
      documents?: unknown[],
    ) {
      return execute('apply_lines', {
        ...b(command),
        ...(picks === 'all' ? { all: true, picks: [] } : { picks }),
        ...(documents ? { documents } : {}),
      }) as Promise<ApplyLinesResponse>
    },
  }
}

export type LineEntryApi = ReturnType<typeof lineEntryApi>

/** A produced line as a details-buffer row: its values plus the provenance
 *  and the resolved limits (control keys both engines strip at save). Rows
 *  the engine flagged keep their errors so the grid highlights them. */
export function toBufferRow(r: LineRowResult): Record<string, unknown> {
  const row: Record<string, unknown> = { ...r.values, _edited: true }
  if (r._link) row._link = r._link
  if (r._limits && Object.keys(r._limits).length) row._limits = r._limits
  if (r._status) row._status = r._status
  if (r._protect && Object.keys(r._protect).length) row._protect = r._protect
  const errs = r._errors || {}
  if (Object.keys(errs).length) row._fieldErrors = errs
  if (r._row_error) row._error = r._row_error
  else if (Object.keys(errs).length) row._error = Object.values(errs)[0]
  return row
}

/** True when the engine answered "I do not know this action" — an older
 *  runtime. The menu then hides itself for the session. */
export function isUnsupportedAction(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message || err || '')
  const detail = JSON.stringify((err as { response?: { data?: unknown } })?.response?.data ?? '')
  return /unknown action/i.test(msg) || /unknown action/i.test(detail)
}
